// Tracked-nutrient table + DRI lookup (NUTRITION_AND_SUBSTANCE_LEVELS_PLAN.md §4.2, D-NUT-3/9/11).
// Pure data/functions only — safe to import from both the API (Lambda) and the SPA.
// Single source of truth for the 32-nutrient panel: USDA numbers, reference intakes,
// and high-density food sources. kcal/fat/carbs deliberately have no `reference`
// row — their targets come from suggestNutritionTargets / Patient.macroTargets.

import type { Patient } from "./types.js";

export type NutrientId =
  // energy + macros (8)
  | "kcal"
  | "protein"
  | "fat"
  | "carbs"
  | "fiber"
  | "sugars"
  | "satFat"
  | "cholesterol"
  // minerals (10)
  | "calcium"
  | "iron"
  | "magnesium"
  | "phosphorus"
  | "potassium"
  | "sodium"
  | "zinc"
  | "copper"
  | "manganese"
  | "selenium"
  // vitamins (12)
  | "vitaminA"
  | "vitaminC"
  | "vitaminD"
  | "vitaminE"
  | "vitaminK"
  | "thiamin"
  | "riboflavin"
  | "niacin"
  | "vitaminB6"
  | "folate"
  | "vitaminB12"
  | "choline"
  // substances (2) — the bridge to substanceLevels.ts
  | "caffeine"
  | "alcohol";

export type NutrientGroup = "energy" | "macro" | "mineral" | "vitamin" | "substance";

export interface NutrientDef {
  id: NutrientId;
  /** USDA FoodData Central nutrientNumber (string, as USDA returns it). */
  usdaNumber: string;
  label: string;
  unit: "kcal" | "g" | "mg" | "mcg";
  group: NutrientGroup;
  /**
   * "meet"  — a target to reach (vitamin C, protein, fiber).
   * "limit" — a ceiling not to exceed (sodium, saturated fat, sugars, cholesterol).
   * "none"  — tracked and displayed, but no recommendation is made (kcal/fat/carbs
   *           get their targets from the patient profile; caffeine has its own
   *           advisory path in substanceLevels; alcohol is shown raw).
   */
  direction: "meet" | "limit" | "none";
  /** Reference intake per day. Keyed by sex then age band. */
  reference?: { maleAdult: number; femaleAdult: number; male51: number; female51: number };
  /** Short, concrete, high-density food sources for the recommendation table. */
  sources?: string[];
}

export const NUTRIENT_DEFS: Record<NutrientId, NutrientDef> = {
  kcal: {
    id: "kcal",
    usdaNumber: "208",
    label: "Calories",
    unit: "kcal",
    group: "energy",
    direction: "none",
    sources: ["mixed nuts", "olive oil", "avocado", "rice", "chicken"],
  },
  protein: {
    id: "protein",
    usdaNumber: "203",
    label: "Protein",
    unit: "g",
    group: "macro",
    direction: "meet",
    reference: { maleAdult: 56, femaleAdult: 46, male51: 56, female51: 46 },
    sources: ["chicken breast", "eggs", "Greek yogurt", "lentils", "tofu"],
  },
  fat: {
    id: "fat",
    usdaNumber: "204",
    label: "Fat",
    unit: "g",
    group: "macro",
    direction: "none",
    sources: ["olive oil", "avocado", "almonds", "salmon", "peanut butter"],
  },
  carbs: {
    id: "carbs",
    usdaNumber: "205",
    label: "Carbohydrates",
    unit: "g",
    group: "macro",
    direction: "none",
    sources: ["rice", "bread", "pasta", "potatoes", "oats"],
  },
  fiber: {
    id: "fiber",
    usdaNumber: "291",
    label: "Fiber",
    unit: "g",
    group: "macro",
    direction: "meet",
    reference: { maleAdult: 38, femaleAdult: 25, male51: 30, female51: 21 },
    sources: ["lentils", "raspberries", "chia seeds", "oats", "black beans"],
  },
  sugars: {
    id: "sugars",
    usdaNumber: "269",
    label: "Sugars",
    unit: "g",
    group: "macro",
    direction: "limit",
    reference: { maleAdult: 50, femaleAdult: 50, male51: 50, female51: 50 },
    sources: ["soda", "candy", "cookies", "ice cream", "fruit juice"],
  },
  satFat: {
    id: "satFat",
    usdaNumber: "606",
    label: "Saturated fat",
    unit: "g",
    group: "macro",
    direction: "limit",
    reference: { maleAdult: 22, femaleAdult: 17, male51: 22, female51: 17 },
    sources: ["butter", "cheese", "coconut oil", "fatty beef", "ice cream"],
  },
  cholesterol: {
    id: "cholesterol",
    usdaNumber: "601",
    label: "Cholesterol",
    unit: "mg",
    group: "macro",
    direction: "limit",
    reference: { maleAdult: 300, femaleAdult: 300, male51: 300, female51: 300 },
    sources: ["egg yolks", "liver", "shrimp", "cheese", "butter"],
  },
  calcium: {
    id: "calcium",
    usdaNumber: "301",
    label: "Calcium",
    unit: "mg",
    group: "mineral",
    direction: "meet",
    reference: { maleAdult: 1000, femaleAdult: 1000, male51: 1000, female51: 1200 },
    sources: ["yogurt", "milk", "fortified tofu", "sardines", "kale"],
  },
  iron: {
    id: "iron",
    usdaNumber: "303",
    label: "Iron",
    unit: "mg",
    group: "mineral",
    direction: "meet",
    reference: { maleAdult: 8, femaleAdult: 18, male51: 8, female51: 8 },
    sources: ["beef liver", "lentils", "spinach", "pumpkin seeds", "fortified cereal"],
  },
  magnesium: {
    id: "magnesium",
    usdaNumber: "304",
    label: "Magnesium",
    unit: "mg",
    group: "mineral",
    direction: "meet",
    reference: { maleAdult: 400, femaleAdult: 310, male51: 420, female51: 320 },
    sources: ["pumpkin seeds", "spinach", "almonds", "black beans", "dark chocolate"],
  },
  phosphorus: {
    id: "phosphorus",
    usdaNumber: "305",
    label: "Phosphorus",
    unit: "mg",
    group: "mineral",
    direction: "meet",
    reference: { maleAdult: 700, femaleAdult: 700, male51: 700, female51: 700 },
    sources: ["yogurt", "salmon", "chicken", "lentils", "pumpkin seeds"],
  },
  potassium: {
    id: "potassium",
    usdaNumber: "306",
    label: "Potassium",
    unit: "mg",
    group: "mineral",
    direction: "meet",
    reference: { maleAdult: 3400, femaleAdult: 2600, male51: 3400, female51: 2600 },
    sources: ["potatoes", "bananas", "spinach", "beans", "yogurt"],
  },
  sodium: {
    id: "sodium",
    usdaNumber: "307",
    label: "Sodium",
    unit: "mg",
    group: "mineral",
    direction: "limit",
    reference: { maleAdult: 2300, femaleAdult: 2300, male51: 2300, female51: 2300 },
    sources: ["table salt", "soy sauce", "processed meat", "canned soup", "pretzels"],
  },
  zinc: {
    id: "zinc",
    usdaNumber: "309",
    label: "Zinc",
    unit: "mg",
    group: "mineral",
    direction: "meet",
    reference: { maleAdult: 11, femaleAdult: 8, male51: 11, female51: 8 },
    sources: ["oysters", "beef", "pumpkin seeds", "lentils", "yogurt"],
  },
  copper: {
    id: "copper",
    usdaNumber: "312",
    label: "Copper",
    unit: "mg",
    group: "mineral",
    direction: "meet",
    reference: { maleAdult: 0.9, femaleAdult: 0.9, male51: 0.9, female51: 0.9 },
    sources: ["beef liver", "oysters", "dark chocolate", "cashews", "lentils"],
  },
  manganese: {
    id: "manganese",
    usdaNumber: "315",
    label: "Manganese",
    unit: "mg",
    group: "mineral",
    direction: "meet",
    reference: { maleAdult: 2.3, femaleAdult: 1.8, male51: 2.3, female51: 1.8 },
    sources: ["mussels", "pineapple", "pecans", "oats", "spinach"],
  },
  selenium: {
    id: "selenium",
    usdaNumber: "317",
    label: "Selenium",
    unit: "mcg",
    group: "mineral",
    direction: "meet",
    reference: { maleAdult: 55, femaleAdult: 55, male51: 55, female51: 55 },
    sources: ["Brazil nuts", "tuna", "sardines", "eggs", "turkey"],
  },
  vitaminA: {
    id: "vitaminA",
    usdaNumber: "320",
    label: "Vitamin A",
    unit: "mcg",
    group: "vitamin",
    direction: "meet",
    reference: { maleAdult: 900, femaleAdult: 700, male51: 900, female51: 700 },
    sources: ["sweet potato", "carrots", "spinach", "liver", "kale"],
  },
  vitaminC: {
    id: "vitaminC",
    usdaNumber: "401",
    label: "Vitamin C",
    unit: "mg",
    group: "vitamin",
    direction: "meet",
    reference: { maleAdult: 90, femaleAdult: 75, male51: 90, female51: 75 },
    sources: ["red bell pepper", "kiwi", "oranges", "strawberries", "broccoli"],
  },
  vitaminD: {
    id: "vitaminD",
    usdaNumber: "328",
    label: "Vitamin D",
    unit: "mcg",
    group: "vitamin",
    direction: "meet",
    reference: { maleAdult: 15, femaleAdult: 15, male51: 15, female51: 15 },
    sources: ["salmon", "fortified milk", "sardines", "egg yolks", "fortified cereal"],
  },
  vitaminE: {
    id: "vitaminE",
    usdaNumber: "323",
    label: "Vitamin E",
    unit: "mg",
    group: "vitamin",
    direction: "meet",
    reference: { maleAdult: 15, femaleAdult: 15, male51: 15, female51: 15 },
    sources: ["sunflower seeds", "almonds", "spinach", "avocado", "olive oil"],
  },
  vitaminK: {
    id: "vitaminK",
    usdaNumber: "430",
    label: "Vitamin K",
    unit: "mcg",
    group: "vitamin",
    direction: "meet",
    reference: { maleAdult: 120, femaleAdult: 90, male51: 120, female51: 90 },
    sources: ["kale", "spinach", "broccoli", "Brussels sprouts", "collard greens"],
  },
  thiamin: {
    id: "thiamin",
    usdaNumber: "404",
    label: "Thiamin (B1)",
    unit: "mg",
    group: "vitamin",
    direction: "meet",
    reference: { maleAdult: 1.2, femaleAdult: 1.1, male51: 1.2, female51: 1.1 },
    sources: ["pork", "sunflower seeds", "black beans", "oats", "fortified cereal"],
  },
  riboflavin: {
    id: "riboflavin",
    usdaNumber: "405",
    label: "Riboflavin (B2)",
    unit: "mg",
    group: "vitamin",
    direction: "meet",
    reference: { maleAdult: 1.3, femaleAdult: 1.1, male51: 1.3, female51: 1.1 },
    sources: ["yogurt", "milk", "almonds", "eggs", "fortified cereal"],
  },
  niacin: {
    id: "niacin",
    usdaNumber: "406",
    label: "Niacin (B3)",
    unit: "mg",
    group: "vitamin",
    direction: "meet",
    reference: { maleAdult: 16, femaleAdult: 14, male51: 16, female51: 14 },
    sources: ["chicken", "tuna", "peanuts", "mushrooms", "fortified cereal"],
  },
  vitaminB6: {
    id: "vitaminB6",
    usdaNumber: "415",
    label: "Vitamin B6",
    unit: "mg",
    group: "vitamin",
    direction: "meet",
    reference: { maleAdult: 1.3, femaleAdult: 1.3, male51: 1.7, female51: 1.5 },
    sources: ["chickpeas", "salmon", "potatoes", "banana", "chicken"],
  },
  folate: {
    id: "folate",
    usdaNumber: "417",
    label: "Folate",
    unit: "mcg",
    group: "vitamin",
    direction: "meet",
    reference: { maleAdult: 400, femaleAdult: 400, male51: 400, female51: 400 },
    sources: ["lentils", "spinach", "asparagus", "chickpeas", "fortified cereal"],
  },
  vitaminB12: {
    id: "vitaminB12",
    usdaNumber: "418",
    label: "Vitamin B12",
    unit: "mcg",
    group: "vitamin",
    direction: "meet",
    reference: { maleAdult: 2.4, femaleAdult: 2.4, male51: 2.4, female51: 2.4 },
    sources: ["clams", "salmon", "beef", "fortified nutritional yeast", "yogurt"],
  },
  choline: {
    id: "choline",
    usdaNumber: "421",
    label: "Choline",
    unit: "mg",
    group: "vitamin",
    direction: "meet",
    reference: { maleAdult: 550, femaleAdult: 425, male51: 550, female51: 425 },
    sources: ["eggs", "beef liver", "soybeans", "chicken", "salmon"],
  },
  caffeine: {
    id: "caffeine",
    usdaNumber: "262",
    label: "Caffeine",
    unit: "mg",
    group: "substance",
    direction: "none",
    sources: ["coffee", "espresso", "black tea", "energy drink", "dark chocolate"],
  },
  alcohol: {
    id: "alcohol",
    usdaNumber: "221",
    label: "Alcohol",
    unit: "g",
    group: "substance",
    direction: "none",
    sources: ["beer", "wine", "spirits", "cider", "liqueur"],
  },
};

export type NutrientPanel = Partial<Record<NutrientId, number>>;

/** Lookup by USDA nutrient number, for the API-side extractor. */
export const NUTRIENT_BY_USDA_NUMBER: Map<string, NutrientDef> = new Map(
  Object.values(NUTRIENT_DEFS).map((d) => [d.usdaNumber, d]),
);

/**
 * Reference intake for one nutrient given a patient's sex + age. Returns
 * undefined for nutrients with no DRI (kcal/fat/carbs — see the §4.2 invariant).
 *
 * When sex is unspecified / intersex / absent, returns the conservative value:
 * the higher of male/female for direction "meet", the lower for "limit" (D-NUT-3).
 * Missing age uses the 19-50 adult band.
 */
export function referenceIntake(
  def: NutrientDef,
  sex: Patient["sex"] | undefined,
  age: number | undefined,
): number | undefined {
  if (!def.reference) return undefined;
  const older = age !== undefined && age >= 51;
  const male = older ? def.reference.male51 : def.reference.maleAdult;
  const female = older ? def.reference.female51 : def.reference.femaleAdult;
  if (sex === "male") return male;
  if (sex === "female") return female;
  if (def.direction === "meet") return Math.max(male, female);
  if (def.direction === "limit") return Math.min(male, female);
  return undefined;
}

/** Scale a per-100g panel to a gram amount, preserving "absent" as absent
 *  (an absent nutrient must NOT become 0 — see D-NUT-9). */
export function scaleNutrientPanel(per100g: NutrientPanel, grams: number): NutrientPanel {
  const f = grams / 100;
  const out: NutrientPanel = {};
  for (const [id, value] of Object.entries(per100g) as Array<[NutrientId, number | undefined]>) {
    if (typeof value === "number" && Number.isFinite(value)) {
      out[id] = value * f;
    }
  }
  return out;
}
